'use client';

import { Search } from "react-feather";
import styles from "@/styles/menu.module.scss";
import { useEffect, useState } from "react";
import { useRef } from "react";
import Image from "next/image";
import { submitCollection, submitSets } from "_services/submit.service";
import { useRouter } from "next/navigation";
import { formatCurrency } from "_services/common.service";
import { useTranslations } from "next-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import RichTextViewer from "./common/richtextviewer";
import { event } from "@lib/ga";

interface ImageObj {
  imageUrl: string;
}

interface SearchResult {
  itemId: string;
  itemName: string;
  imageUrls: ImageObj[];
  price: { amount: number; currency: string };
  itemDescription: string;
  isVegetarian: boolean;
  isSoldOut: boolean;
}

export default function SearchBar() {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchCursor, setSearchCursor] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isEndReached, setIsEndReached] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchMenuRef = useRef<HTMLDivElement>(null);

  const t = useTranslations("SearchBar");

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    if (searchQuery) {
      setIsOpen(true);
      searchTimeout.current = setTimeout(() => {
        setIsEndReached(false);
        performSearch(searchQuery, false, 0);
      }, 500); // Debounce search input
    } else {
      setSearchResults([]);
      setIsOpen(false);
    }
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const performSearch = async (query: string, isLoadMore: boolean, startIndex: number) => {
    try {
      setIsLoading(true);

      if(!isLoadMore){
        setSearchResults([]);
      }

      const response = await submitSets(submitCollection.loadMenu, { 
        "categoryIds": [],
        "isReqPagination": true,
        "startIndex": (isLoadMore ? startIndex : 0),
        "maxResults": 10,
        "searchQuery": query,
        "tag": "",
        "cursor": (isLoadMore && searchCursor != "" ? searchCursor : "")
      }, false, null, false);

      // console.log('Search results:', response);
      
      if (response && response.status && response.extra && Array.isArray(response.extra)) {
        setSearchCursor(response.cursor || "");

        const resultsList = (response.extra as SearchResult[]);

        setIsEndReached(!response.cursor && (startIndex > 0 || 
          (resultsList.length > 0 && resultsList.length <= (startIndex + 10))
        ));

        setTotalCount(response.count > 0 ? response.count : startIndex === 0? 0 : totalCount);

        if (isLoadMore) {
          setSearchResults((prevResults) => [...prevResults, ...resultsList]);
        } else {
          setSearchResults(resultsList);
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (itemName: string) => {
    router.push(`/item/${itemName}`);
  };

  const handleScrollPagination = async (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    
    if (((target.scrollHeight - target.scrollTop) - 1) <= target.clientHeight) {
      // console.log('Scrolled to bottom, load more results if available');
      
      // Load more search results
      if(isLoading || searchResults.length < 10 || totalCount === searchResults.length || isEndReached) return; // Prevent multiple calls or if less than page size

      await performSearch(searchQuery, true, searchResults.length);
    }
  };

  // console.log('searchResults:', searchResults);
  // console.log(isEndReached);
  
  

  return (<>
    <div className={styles.searchContainer} ref={searchContainerRef}>
      <div className={styles.searchBar + ' ' + (isOpen ? styles['show-suggestions'] : '')}>
        <Search size={20} />
        <input 
          id="nav-search-input"
          type="text" 
          placeholder={t('SEARCH_PLACEHOLDER')}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {/* dropdown for search suggestions */}
      {isOpen && (
        <div className={styles.suggestions} ref={searchMenuRef} onScroll={(e) => handleScrollPagination(e)}>
          {searchResults.length > 0 ? (
            searchResults.map((item: SearchResult, index: number) => (
              <div
                id={`nav-search-suggestion-${index}`}
                key={index}
                className={styles.suggestionItem + ' ' + (item.isSoldOut ? styles.outOfStock : '')}
                onClick={() => {
                  handleItemClick(item.itemName);
                  event({
                    action: 'select_search_result',
                    category: 'engagement',
                    label: `search_result_${item.itemName}_clicked`
                  });
                }}
              >
                <div className={styles.imageWrapper}>
                  <Image
                    src={item?.imageUrls?.[0]?.imageUrl || '/assets/images/placeholder.png'}
                    alt={item.itemName}
                    width={50}
                    height={50}
                    className={styles.itemImage}
                    onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/placeholder.png'; }}
                  />
                </div>
                <div className={styles.itemInfo}>
                  <div className={styles.headerTitle}>
                    <h4>{item.itemName}</h4>
                    
                    <OverlayTrigger
                      overlay={(props) => <Tooltip {...props}>{item?.isVegetarian ? t('VEGETARIAN') : t('NON_VEGETARIAN')}</Tooltip>}
                    >
                      {/* <div className={styles.mealPrep + (item?.isVegetarian ? ` ${styles.veg}` : '')}>
                        <span className={styles.dot}></span>
                      </div> */}
                      <div className={styles.mealPrep  + (item?.isVegetarian === null ? `${styles.hidden}`
                        : `${item?.isVegetarian ? ` ${styles.veg}` : ''}`)}>
                        <span className={styles.dot}></span>
                      </div>
                    </OverlayTrigger>
                  </div>
                  
                  <RichTextViewer html={item.itemDescription.substring(0, 50) + (item.itemDescription.length > 50 ? '...' : '')} />

                  <div className={styles.priceContainer}>
                    <span className={styles.price}>{formatCurrency((item?.price?.amount || 0), (item?.price?.currency || 'USD'))}</span>
                    <div className={styles.itemTags}>
                      {item.isSoldOut && <span className={styles.tag}>{t('SOLD_OUT')}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (!isLoading && searchQuery) && (
            <div className={styles.noResults}>
              <span>{t('NO_RESULTS')}</span>
            </div>
          )}

          {isLoading ? (
            <div className={styles.loading}>
              <span>{t('SEARCHING')}</span>
            </div>
          ) : null} 

          {isEndReached && (
            <div className={styles.endReached}>
              <span>{t('END_OF_RESULTS')}</span>
            </div>
          )}

        </div>
      )}
    </div>
  </>);
}